/**
 * @author Antoine Laviron
 * @date 2024-08-27
 *
 * @description
 * This component serves as the login page for the application. It orchestrates
 * the start of the OAuth 2.0 PKCE (Proof Key for Code Exchange) flow.
 * Its primary function is to redirect the user to the Salesforce authorization
 * page with the appropriate parameters.
 */

import React, { useEffect, useState } from 'react';

function Login() {
    // State to store the Salesforce configuration fetched from the backend.
    const [config, setConfig] = useState({ salesforceUrl: '', clientId: '' });
    const [isLoading, setIsLoading] = useState(true);
    
    // State to handle and display any errors that occur.
    const [error, setError] = useState('');

  // useEffect hook to fetch the Salesforce configuration from the backend API
  // as soon as the component mounts.    
    useEffect(() => {
        fetch('/api/config')
            .then(response => {
                if (!response.ok) {
                    throw new Error(`Network response was not ok: ${response.statusText}`);
                }
                return response.json();
            })
            .then(data => {
                // Correctly map the actual backend response keys to our state properties
                setConfig({
                    salesforceUrl: data.instanceUrl,
                    clientId: data.clientId
                });
                setIsLoading(false); // Mark loading as complete
            })
            .catch(error => {
                console.error("Failed to fetch config:", error);
                setError('Could not load configuration from server. Please try again later.');
                setIsLoading(false); // Mark loading as complete even on error
            });
    }, []);

    // --- PKCE Helper Functions ---
    function base64URLEncode(array) {
        return btoa(String.fromCharCode.apply(null, array))
            .replace(/\+/g, '-')
            .replace(/\//g, '_')
            .replace(/=/g, '');
    }

    function generateCodeVerifier() {
        const array = new Uint8Array(32);
        window.crypto.getRandomValues(array);
        return base64URLEncode(Array.from(array));
    }

    async function generateCodeChallenge(verifier) {
        const encoder = new TextEncoder();
        const data = encoder.encode(verifier);
        const hashBuffer = await window.crypto.subtle.digest('SHA-256', data);
        return base64URLEncode(Array.from(new Uint8Array(hashBuffer)));
    }
    // --- End of PKCE Helpers ---

    /**
     * Initiates the login process by redirecting the user to Salesforce.
     */
    const handleLogin = async () => {
        // Prevent login attempt if the configuration has not yet been loaded.
        try {
            // PCKE Flow Setup
            // 1. Generate a cryptographically random `code_verifier`.
            const codeVerifier = generateCodeVerifier();

            // 2. Create a `code_challenge` by SHA256-hashing the verifier.
            const codeChallenge = await generateCodeChallenge(codeVerifier);
            
            // 3. Store the `code_verifier` in sessionStorage. It is needed later in the
            //    /callback route to exchange the authorization code for an access token.
            //    sessionStorage is used because it's secure and persists for the duration
            //    of the page session.
            sessionStorage.setItem('code_verifier', codeVerifier);
            sessionStorage.setItem('client_id', config.clientId);

            // --- Construct Redirect URL ---
            const redirectUri = `${window.location.origin}/callback`;

            // --- Construct Authorization URL ---
            // The user will be redirected to this URL to grant the application access.
            const authUrl = `${config.salesforceUrl}/services/oauth2/authorize?` +
                `response_type=code&` +
                `client_id=${config.clientId}&` +
                `redirect_uri=${encodeURIComponent(redirectUri)}&` +
                `scope=web lightning api&` +
                `prompt=login&` +
                `code_challenge=${codeChallenge}&` +
                `code_challenge_method=S256`;
        
            // 4. Redirect the user's browser to the Salesforce authorization endpoint.
            window.location.href = authUrl;

        } catch (err) {
            console.error("Error during login initiation:", err);
            setError("Failed to initiate login. Please check the console for errors.");
        }
    };

    return (
        <div className="container">
            <h1>Tableau Next Embedding</h1>
            <p>Please log in to access the demo.</p>
            {error && <p className="error-message">{error}</p>}
            <button 
                onClick={handleLogin} 
                className="login-button" 
                disabled={isLoading || !config.salesforceUrl || !config.clientId}
            >
                {isLoading ? 'Loading...' : 'Login with Salesforce'}
            </button>
        </div>
    );
}

export default Login;

